import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import { LoadingOverlay } from '../ui';
import type { Permission } from '../../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If specified, user must have at least one of these permissions */
  requiredPermissions?: Permission[];
  /** If true, user must have ALL specified permissions (default: false = any) */
  requireAll?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions,
  requireAll = false,
}) => {
  const location = useLocation();
  const { isAuthenticated, token, validateSession, hasAnyPermission, hasAllPermissions, user } =
    useAuthStore();
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        await validateSession();
      }
      setIsValidating(false);
    };

    checkAuth();
  }, [token, validateSession]);

  if (isValidating) {
    return <LoadingOverlay message="Validating session..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if specified (sysadmin skips this)
  if (requiredPermissions && requiredPermissions.length > 0 && !user?.isSuperAdmin) {
    const hasPermission = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasPermission) {
      // Redirect to unauthorized page or dashboard
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;



